import OktaAuth, { TokenManagerError, toRelativeUrl } from "@okta/okta-auth-js";
import { Security } from "@okta/okta-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import { LoadingOverlayIndicator } from "../components/loading-overlay-indicator";
import { OKTA_CLIENT_ID, OKTA_ISSUER } from "../lib/env-helpers";

export const OktaSecurity: React.FC<{ oktaAuth?: OktaAuth }> = ({ children, oktaAuth: _oktaAuth }) => {
  const navigate = useNavigate();

  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const restoreOriginalUri = useCallback(
    async (_, originalUri: string | null) => {
      /** Global Error Boundary shows logout if user is logged in */
      /** Event notifies Global Error Boundary that user is logged in  */
      /** Otherwise localstorage update is not caught by component */
      window.dispatchEvent(new Event("storage"));
      navigate(toRelativeUrl(originalUri ?? "/", window.location.origin), {
        replace: true,
      });
    },
    [navigate],
  );

  const oktaAuth = useMemo(
    () =>
      _oktaAuth ??
      new OktaAuth({
        issuer: OKTA_ISSUER,
        clientId: OKTA_CLIENT_ID,
        redirectUri: `${window.location.origin}/login/callback`,
        pkce: true,
        // Safari's Intelligent Tracking Prevention blocks third-party cookies by default.
        // When refreshToken is undefined, Okta attempts to renew the token silently via an iframe.
        // However, this iframe-based silent renewal is blocked by Safari, leading to a "login_required" error and forcing the user to log out.
        // To avoid this, ensure a refresh token is configured and used for token renewal instead.
        scopes: ["openid", "email", "offline_access"],
        tokenManager: {
          autoRenew: true,
        },
      }),
    [_oktaAuth],
  );

  // When the app is idle for a certain period, we receive an "invalid_grant" error due to refresh token expiration.
  // If we don't explicitly log out the user at this point, navigating to the login page will cause Okta to automatically
  // re-authenticate the user using a still-active Okta session, resulting in automatic login.
  useEffect(() => {
    const handleError = async (err: TokenManagerError) => {
      if (err.name === "OAuthError" && err.errorCode === "invalid_grant") {
        setIsLoggingOut(true);
        try {
          await oktaAuth.signOut({
            clearTokensBeforeRedirect: true,
            postLogoutRedirectUri: window.location.origin + "/",
          });
        } catch (e) {
          console.error("Token expiry signOut error", e);
        }
      }
    };

    oktaAuth.tokenManager.on("error", handleError);

    return () => {
      oktaAuth.tokenManager.off("error", handleError);
    };
  }, [oktaAuth, navigate]);

  const onAuthRequired = useCallback(() => {
    navigate("/", { replace: true });
  }, [navigate]);

  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri} onAuthRequired={onAuthRequired}>
      {isLoggingOut ? <LoadingOverlayIndicator /> : children}
    </Security>
  );
};
